import { getSurvey } from '../../../../../services/data-entry/surveys.service'
import IconTooltipButton from '../../../../../components/button/IconTooltipButton'

const ExportResponsesButton = ({ survey, surveyId, isExporting, setIsExporting }) => {
  const { type } = survey || {}

  const fetchSurveyResponses = async (surveyId) => {
    try {
      const response = await getSurvey(surveyId, true)

      return response?.data
    } catch (error) {
      console.error('Error fetching survey responses:', error)
      throw error
    }
  }

  const formatDate = () => {
    return new Date().toISOString().split('T')[0] // YYYY-MM-DD format
  }

  const formatTimeUTC = (isoDate) => {
    try {
      if (!isoDate) return ''
      const date = new Date(isoDate)

      const hours = date.getUTCHours().toString().padStart(2, '0')
      const minutes = date.getUTCMinutes().toString().padStart(2, '0')

      return `${hours}:${minutes}`
    } catch (error) {
      return ''
    }
  }

  const stripHtml = (html) => {
    if (!html) return ''
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim()
  }

  const getQuestionText = (question) => {
    if (question.title) return stripHtml(question.title)
    if (question.content) return stripHtml(question.content)
    return `Question ${question.order}`
  }

  const getOptionText = (question, optionValue) => {
    if (!question.options || !Array.isArray(question.options)) return optionValue
    const option = question.options.find(
      (opt) => opt.value === optionValue || opt.id?.toString() === optionValue
    )
    return option ? stripHtml(option.option) : optionValue
  }

  const generateCSVContent = (surveyData) => {
    const { template, users = [] } = surveyData

    // For about-type surveys, flatten dataResults from all teachers/targets.
    // For standard surveys, use the enriched users array (each user has user.result).
    const isAboutType = type && type !== 'standard'
    const usersList = isAboutType
      ? (surveyData?.data?.data || []).flatMap((target) => target?.dataResults || [])
      : users?.length > 0
        ? users
        : []

    if (!usersList || usersList.length === 0) {
      return 'Response ID,Message\n1,"No responses available for this survey"'
    }

    // Get only users who have completed the survey
    const completedUsers = usersList?.filter((user) => user?.isCompleted || user?.isSubmitted)

    if (completedUsers.length === 0) {
      return 'Response ID,Message\n1,"No completed responses available for this survey"'
    }

    // Filter out groupreference questions (they're just content/instructions)
    const actualQuestions = template.questions.filter((q) => q.type.value !== 'groupreference')

    // Create headers
    const headers = [
      'User Code',
      'First Name',
      'Last Name',
      'Preferred Name',
      'User Email',
      'Form/Class',
      'House',
      'Date Submitted',
      'Survey Type'
    ]

    // Add question headers with proper escaping
    actualQuestions.forEach((question, index) => {
      const questionText = getQuestionText(question)
      // Escape quotes and wrap in quotes to handle commas, quotes, and newlines
      const safeQuestionText = questionText.replace(/"/g, '""')
      headers.push(`Q${index + 1}: ${safeQuestionText}`)
    })

    // Properly escape and quote all headers
    const escapedHeaders = headers.map((header) => {
      const escaped = header.replace(/"/g, '""')
      return `"${escaped}"`
    })

    const csvRows = [escapedHeaders.join(',')]

    // Process each completed response
    completedUsers.forEach((user) => {
      const result = user.result || user
      const answers = user?.answers || result?.answers || []

      const userCode = type === 'standard' ? user?.code : result?.code || result?.userData?.code || ''
      const firstName =
        type === 'standard'
          ? user?.firstName
          : result?.firstName || result?.userData?.firstName || user?.name || ''
      const lastName =
        type === 'standard' ? user?.lastName : result?.lastName || result?.userData?.lastName || ''
      const preferredName =
        type === 'standard'
          ? user?.preferredName
          : result?.preferredName || result?.userData?.preferredName || ''
      const email = type === 'standard' ? user?.email : result?.email || result?.userData?.email || ''
      const form = type === 'standard' ? user?.data?.form : result?.userData?.data?.form || ''
      const house = type === 'standard' ? user?.data?.house : result?.userData?.data?.house || ''
      const dateFilledOut = result?.dateFilledOut || ''

      const row = [
        `"${userCode || ''}"`,
        `"${firstName || ''}"`,
        `"${lastName || ''}"`,
        `"${preferredName || ''}"`,
        `"${email || ''}"`,
        `"${form || ''}"`,
        `"${house || ''}"`,
        `"${dateFilledOut ? new Date(dateFilledOut)?.toLocaleDateString() : ''}"`,
        `"${type || ''}"`
      ]

      // Add answers for each question
      actualQuestions.forEach((question) => {
        const answer = answers?.find((a) => a?.questionId === question?.id)
        let answerText = ''

        if (answer && answer?.answer) {
          const answerArray = Array.isArray(answer.answer) ? answer.answer : [answer.answer]

          switch (question.type.value) {
            case 'singlechoice':
              // For single choice, get the option text
              if (answerArray?.length > 0) {
                answerText = getOptionText(question, answerArray[0])
              }
              break

            case 'multiplechoice':
              // For multiple choice, join all selected options
              // eslint-disable-next-line no-case-declarations
              const selectedOptions = answerArray
                .filter((val) => val && val !== question.id) // Filter out the question ID itself
                .map((val) => getOptionText(question, val))
              answerText = selectedOptions.join(', ')
              break

            case 'shortanswer':
            case 'longanswer':
              answerText = answerArray.join(' ')
              break

            case 'time':
              answerText = formatTimeUTC(answerArray?.[0])
              break

            case 'date':
              answerText = new Date(answerArray?.[0]).toLocaleDateString()
              break

            default:
              answerText = answerArray.filter((val) => val && val !== question.id).join(', ')
          }
        }

        // Clean and escape the answer for CSV
        if (typeof answerText === 'string') {
          answerText = answerText.replace(/"/g, '""')
        }
        row.push(`"${answerText}"`)
      })

      csvRows.push(row.join(','))
    })

    return csvRows.join('\n')
  }

  const downloadCSV = (content, filename) => {
    const BOM = '\uFEFF' // UTF-8 BOM for proper encoding
    const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', filename)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }

  const handleExport = async () => {
    if (!surveyId) {
      console.error('Survey ID is missing')
      return
    }

    setIsExporting(true)

    try {
      // Fetch survey data with responses
      const surveyData = await fetchSurveyResponses(surveyId)

      // Generate CSV content
      const csvContent = generateCSVContent(surveyData)

      // Create filename
      const currentDate = formatDate()
      const surveyName = (surveyData.name || surveyData.template?.title || 'survey')
        .replace(/[^a-z0-9\s]/gi, '') // Remove special characters except spaces
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .toLowerCase()
      const filename = `${surveyName}_responses_${currentDate}.csv`

      // Download the file
      downloadCSV(csvContent, filename)

      console.log(`Exported ${surveyData.totalCompleted || 0} responses successfully`)
    } catch (error) {
      console.error('Error exporting responses:', error)
      // You might want to show a toast notification here
      alert('Error exporting survey responses. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <IconTooltipButton
      id={`export-responses-${surveyId}`}
      disabled={isExporting}
      title={isExporting ? 'Exporting...' : 'Export Responses'}
      icon={'fas fa-download'}
      onClick={handleExport}
    />
  )
}

export default ExportResponsesButton
